import { CollectionSort, Network, NftSort } from '@mogul/api-client';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { collectionsApi, nftsApi } from '../../api';
import { isProduction } from '../../config';
import { Web3StateContext } from '../../contexts/app-state-context/web3-state/context';
import { useWeb3State } from '../../hooks';
import { initHideOnScrollEffect } from '../../utils/searchbar.util';
import styles from './SearchBar.module.css';
import { SearchBarInput } from './SearchBarInput.component';

interface Props {
  setSearch?: React.Dispatch<React.SetStateAction<string>>;
  tailwindOverride?: String;
  viewableHeightMobile?: number;
  viewableHeightDesktop?: number;
}

export const SearchBar: React.FC<Props> = props => {
  const [search, setSearch] = useState('');
  const [delay, setDelay] = useState('');
  const [nfts, setNfts] = useState<any>([]);
  const [collections, setCollections] = useState<any>([]);
  const [isFocused, setIsFocused] = useState(false);

  const { walletDetails } = useWeb3State();

  const history = useHistory();

  const getMarketplaces = () => {
    return isProduction
      ? [Network.Mainnet, Network.Binance]
      : [Network.Rinkeby, Network.Bsctest];
  };

  const getMarketplaceAbr = (chainId: string | null) => {
    if (chainId === 'RINKEBY' || chainId === 'MAINNET') {
      return 'eth';
    } else {
      return 'bsc';
    }
  };

  const goToCollection = (collection: any) => {
    const prevUrl = window.location.href;

    history.push(
      `/platform/collection/${getMarketplaceAbr(walletDetails.chain.type)}/${
        collection.contractAddress
      }`,
    );

    if (prevUrl.includes('collection')) {
      history.go(0);
    }
  };

  const goToNft = (nft: any) => {
    const prevUrl = window.location.href;
    const path = `/platform/nftDetails/${getMarketplaceAbr(
      walletDetails.chain.type,
    )}/${nft.contractAddress}/${nft.id}`;

    history.push(path);

    if (prevUrl.includes('nftDetails')) {
      history.go(0);
    }
  };

  const updateSearch = (e: React.FormEvent<HTMLInputElement>) => {
    // the delay gives the api sometime to catch up before displaying no results
    const target = e.target as HTMLInputElement;

    const timeoutId = setTimeout(() => {
      setDelay(target.value);
    }, 400);
    setSearch(target.value);
    if (props.setSearch) {
      props.setSearch(target.value);
    }
    return () => clearTimeout(timeoutId);
  };

  const clearSearch = () => {
    // the delay gives the api sometime to catch up before displaying no results
    const timeoutId = setTimeout(() => {
      setDelay('');
    }, 400);
    setSearch('');
    if (props.setSearch) {
      props.setSearch('');
    }
    setIsFocused(false);
    return () => clearTimeout(timeoutId);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search.length > 0) {
        void collectionsApi
          .listCollections({
            limit: 4,
            offset: 0,
            searchQuery: search,
            sort: CollectionSort.NameDesc,
            network: getMarketplaces(),
          })
          .then(results => {
            setCollections(results.data.data);
          });
        void nftsApi
          .listMintedNfts({
            limit: 4,
            offset: 0,
            searchQuery: search,
            sort: NftSort.TitleDesc,
            network: getMarketplaces(),
          })
          .then(results => {
            setNfts(results.data.data);
          });
      } else {
        setCollections([]);
        setNfts([]);
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    initHideOnScrollEffect(
      'searchContainer',
      props.viewableHeightMobile ?? 95,
      props.viewableHeightDesktop ?? 220,
    );
  }, []);

  const onFocus = () => {
    setIsFocused(true);
    const viewPortWidth = window.innerWidth;
    const app = document.querySelector<HTMLElement>('.app-container');
    const searchContainer = document.getElementById('searchContainer');

    if (app && searchContainer && viewPortWidth < 640) {
      if (app.scrollTop < (props.viewableHeightMobile ?? 95)) {
        app.scrollTo({
          top: props.viewableHeightMobile ?? 95,
          behavior: 'smooth',
        });
      }
      searchContainer.style.top = '3rem';
      app.style.overflow = 'hidden';
    }
  };

  const wrapperRef = useRef(null);

  const useOutsideAlerter = (ref: { current: any }) => {
    useEffect(() => {
      function handleClickOutside(event: { target: any }) {
        const app = document.querySelector<HTMLElement>('.app-container');
        if (ref.current && !ref.current.contains(event.target)) {
          if (app) {
            app.style.overflow = '';
          }
          setIsFocused(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  };

  useOutsideAlerter(wrapperRef);

  return (
    <div
      ref={wrapperRef}
      className={`${props.tailwindOverride} sticky sm:top-0 top-12 flex lg:flex-row flex-col justify-between z-20`}
      id="searchContainer"
    >
      <SearchBarInput
        search={search}
        value={search}
        onChange={updateSearch}
        onFocus={onFocus}
        onClick={clearSearch}
      />

      <div
        className={
          styles.resultBox +
          ' absolute w-full h-auto md:top-14 sm:top-12 top-16 left-0 z-20'
        }
      >
        {collections.length === 0 &&
        nfts.length === 0 &&
        search.length > 0 &&
        delay.length > 0 &&
        isFocused ? (
          <div>
            <hr className={styles.break + ' m-0'} />
            <div className={styles.collectionList + ' my-2 text-xs'}>
              No Results Found :(
            </div>
          </div>
        ) : null}
        {collections.length > 0 && isFocused ? (
          <div>
            <hr className={styles.break + ' m-0'} />
            <div className={styles.collectionList + ' my-2 text-xs'}>
              Collections
            </div>
            {collections.map((collection: any) => {
              return (
                <div key={`${collection.id}${collection.contractAddress}`}>
                  <hr className={styles.divider + ' m-0'} />
                  <div
                    role="menu"
                    tabIndex={0}
                    aria-hidden="true"
                    className={
                      'flex flex-row items-center w-full cursor-pointer'
                    }
                    onClick={() => goToCollection(collection)}
                  >
                    <img
                      className={styles.miniProfile + ' ml-5 md:my-4 my-3'}
                      src={collection.logoImageUrl}
                      alt="nftPrv"
                      width="30px"
                      height="30px"
                    />
                    <div className={'ml-6 text-sm font-bold'}>
                      {collection.name}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        {nfts.length > 0 && search.length > 0 && isFocused ? (
          <div>
            <hr className={styles.divider + ' m-0'} />
            <div className={styles.collectionList + ' my-2 text-xs'}>
              Digital Collectibles
            </div>
            {nfts.map((nft: any) => {
              return (
                <div key={nft.id}>
                  <hr className={styles.divider + ' m-0'} />
                  <div
                    role="menu"
                    tabIndex={0}
                    aria-hidden="true"
                    className={
                      'flex flex-row items-center w-full cursor-pointer'
                    }
                    onClick={() => goToNft(nft)}
                  >
                    {nft.imagePreview.mimeType === 'video/mp4' ? (
                      <div
                        className={styles.miniVideoWrap + ' ml-5 md:my-4 my-3'}
                      >
                        <video
                          className={styles.miniVideo}
                          width="28px"
                          height="28px"
                        >
                          <source src={nft.imagePreview.publicUrl} />
                        </video>
                      </div>
                    ) : (
                      <img
                        className={styles.miniProfile + ' ml-5 md:my-4 my-3'}
                        src={nft.imagePreview.publicUrl}
                        alt="nftPrv"
                        width="30px"
                        height="30px"
                      />
                    )}

                    <div className={'ml-6 text-sm font-bold'}>{nft.title}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {search.length > 0 && isFocused ? (
          <hr className={styles.divider + ' m-0'} />
        ) : null}
        {search.length > 0 &&
        isFocused &&
        (nfts.length > 0 || collections.length > 0) ? (
          <div>
            <div className={'sm:h-0 h-96'} />
            <div className={'sm:h-0 h-96'} />
          </div>
        ) : null}
      </div>
    </div>
  );
};
